/**
 * Per-account Djay variant selection hook.
 *
 * Scopes the selected mascotte variant to the active learner account.
 * When switching accounts, each account gets their own variant (or defaults to 'default').
 *
 * DDD co-locatie: moved from components/mascot/ in s60.
 * Fase 2 dataLayer migration: localStorage calls replaced by dataLayer.
 *
 * REACTIVITY FIX: Uses a module-level store with listeners so ALL consumers
 * of useDjaySelection() stay in sync when any one of them changes the variant.
 *
 * Audit 2026: dubbele effect (mount + prev-ref) samengevoegd tot één
 * effect met [accountId] deps (H8). Zie docs/audits/MASCOT_DOMAIN_AUDIT_2026.md §3.1.
 */

import { useCallback, useEffect, useSyncExternalStore } from 'react'
import { DjayVariant } from './DjayAvatar'
import { useAppStore } from '../app/hooks/useAppStore'
import { dataLayer } from '../data/dataLayer'

const DEFAULT_VARIANT: DjayVariant = 'default'

// ── Module-level shared store ────────────────────────────────────────
// All useDjaySelection() instances subscribe to this store so changes
// in FloatingDjay are instantly reflected in ProgressPage and vice versa.

type Listener = () => void

let _currentVariant: DjayVariant = DEFAULT_VARIANT
let _currentAccountId: string | null = null
/**
 * Monotonic generation token. Bumped on every account-switch load AND
 * on every explicit `_setVariant` write. An in-flight load only applies
 * its result if the generation hasn't moved — this guards against the
 * "user picks variant before initial load resolves" race that would
 * otherwise overwrite the user's choice with the stored value.
 * (Audit RC ronde 2 2026.)
 */
let _generation = 0
const _listeners = new Set<Listener>()

function getSnapshot(): DjayVariant {
  return _currentVariant
}

function subscribe(listener: Listener): () => void {
  _listeners.add(listener)
  return () => {
    _listeners.delete(listener)
  }
}

function _notify() {
  _listeners.forEach((l) => l())
}

function _setVariant(variant: DjayVariant, accountId: string | null) {
  _generation += 1
  _currentVariant = variant
  _notify()
  void dataLayer.saveDjayVariant(
    accountId,
    variant === DEFAULT_VARIANT ? null : variant,
  )
}

async function _loadVariantForAccount(accountId: string | null) {
  _generation += 1
  const gen = _generation
  _currentAccountId = accountId
  const stored = await dataLayer.getDjayVariant(accountId)
  // Apply only if neither (a) the account changed nor (b) a write
  // happened during the await — both bump the generation.
  if (_currentAccountId === accountId && _generation === gen) {
    _currentVariant = (stored as DjayVariant) || DEFAULT_VARIANT
    _notify()
  }
}

// ── Public hook ──────────────────────────────────────────────────────

export function useDjaySelection() {
  const { activeAccount } = useAppStore()
  const accountId = activeAccount?.id ?? null

  // Single effect: load on mount AND on account-switch.
  // Replaces the previous double-effect pattern (mount with [] + prev-ref
  // with [accountId]) which caused a duplicate load on first render.
  useEffect(() => {
    _loadVariantForAccount(accountId)
  }, [accountId])

  // Subscribe to the shared store
  const selectedVariant = useSyncExternalStore(subscribe, getSnapshot)

  const setSelectedVariant = useCallback(
    (variant: DjayVariant) => {
      _setVariant(variant, accountId)
    },
    [accountId],
  )

  return { selectedVariant, setSelectedVariant }
}
